import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  Alert, CircularProgress, Card, CardContent, Grid, Tabs, Tab, Divider,
  Switch, FormControlLabel, Tooltip, IconButton
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaletteIcon from '@mui/icons-material/Palette';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';
import authService from '../../services/authService';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Lab {
  id: string;
  lab_id: string;
  name: string;
  integration_count: number;
}

interface Integration {
  id: string;
  lis_system: string;
  integration_type: string;
  config: Record<string, any>;
  is_active: boolean;
  last_connected_at: string | null;
  last_error: string | null;
}

interface LabDetail {
  lab_id: string;
  name: string;
  logo_url: string | null;
  branding_config: Record<string, any> | null;
  integrations: Integration[];
}

interface ReportStatus {
  id: string;
  report_id: string;
  lis_system: string;
  is_pushed: boolean;
  push_method: string | null;
  push_attempts: number;
  last_error: string | null;
  panel_type: string | null;
  specimen_type: string | null;
  file_size_bytes: number | null;
  processing_time_ms: number | null;
  pushed_at: string | null;
  created_at: string | null;
}

const IntegrationSetup: React.FC = () => {
  const [labs, setLabs] = useState<Lab[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Selected lab & integration
  const [selectedLab, setSelectedLab] = useState<LabDetail | null>(null);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [mainTab, setMainTab] = useState(0);

  // Connection form
  const [connForm, setConnForm] = useState<Record<string, any>>({});
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  // HL7 settings form
  const [hl7Form, setHl7Form] = useState({
    hl7_version: '2.5.1', hl7_encoding: 'UTF-8',
    field_separator: '|', component_separator: '^',
    repetition_separator: '~', escape_character: '\\',
    subcomponent_separator: '&', segment_separator: '\\r',
  });

  // Report delivery form
  const [deliveryForm, setDeliveryForm] = useState({
    push_method: 'base64', auto_push: true,
    file_pattern: 'report_{report_id}', pdf_max_size_mb: '10',
    polling_interval_minutes: '5',
  });

  // Branding
  const [brandingForm, setBrandingForm] = useState({
    primary_color: '#1976d2', secondary_color: '#dc004e',
    report_header_text: '',
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  // Report status
  const [reportStatuses, setReportStatuses] = useState<ReportStatus[]>([]);
  const [reportSummary, setReportSummary] = useState({ total: 0, pushed: 0, pending: 0, errors: 0 });
  const [statusFilter, setStatusFilter] = useState('');

  const getHeaders = () => authService.getAuthHeaders();

  // ── Fetch Labs ──────────────────────────────────────────────

  const fetchLabs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/v1/lis-portal/labs`, { headers: getHeaders() });
      if (response.data.success) setLabs(response.data.labs || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch labs');
    } finally {
      setLoading(false);
    }
  };

  const fetchLabDetail = async (labId: string) => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/lis-portal/labs/${labId}`, { headers: getHeaders() });
      if (response.data.success) {
        const lab = response.data.lab;
        setSelectedLab(lab);
        if (lab.integrations?.length > 0) {
          selectIntegration(lab.integrations[0], lab);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch lab details');
    }
  };

  const selectIntegration = (integration: Integration, lab?: LabDetail) => {
    setSelectedIntegration(integration);
    const config = integration.config || {};

    // Populate connection form
    setConnForm({ ...config });

    // Populate HL7 settings from config
    setHl7Form({
      hl7_version: config.hl7_version || '2.5.1',
      hl7_encoding: config.hl7_encoding || 'UTF-8',
      field_separator: config.field_separator || '|',
      component_separator: config.component_separator || '^',
      repetition_separator: config.repetition_separator || '~',
      escape_character: config.escape_character || '\\',
      subcomponent_separator: config.subcomponent_separator || '&',
      segment_separator: config.segment_separator || '\\r',
    });

    // Populate delivery settings
    setDeliveryForm({
      push_method: config.push_method || 'base64',
      auto_push: config.auto_push !== false,
      file_pattern: config.file_pattern || 'report_{report_id}',
      pdf_max_size_mb: String(config.pdf_max_size_mb || '10'),
      polling_interval_minutes: String(config.polling_interval_minutes || '5'),
    });

    // Populate branding
    const currentLab = lab || selectedLab;
    const branding = currentLab?.branding_config || {};
    setBrandingForm({
      primary_color: branding.primary_color || '#1976d2',
      secondary_color: branding.secondary_color || '#dc004e',
      report_header_text: branding.report_header_text || '',
    });
    setLogoPreview(currentLab?.logo_url || null);
  };

  useEffect(() => { fetchLabs(); }, []);

  // ── Save Config ─────────────────────────────────────────────

  const saveConnectionConfig = async () => {
    if (!selectedLab || !selectedIntegration) return;
    setSaving(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/v1/lis-portal/labs/${selectedLab.lab_id}/integrations/${selectedIntegration.id}/config`,
        { config: connForm },
        { headers: getHeaders() }
      );
      if (response.data.success) setSuccess('Connection settings saved');
      else setError(response.data.error);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveHl7Config = async () => {
    if (!selectedLab || !selectedIntegration) return;
    setSaving(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/v1/lis-portal/labs/${selectedLab.lab_id}/integrations/${selectedIntegration.id}/config`,
        { config: hl7Form },
        { headers: getHeaders() }
      );
      if (response.data.success) setSuccess('HL7 settings saved');
      else setError(response.data.error);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveDeliveryConfig = async () => {
    if (!selectedLab || !selectedIntegration) return;
    setSaving(true);
    try {
      const config = {
        ...deliveryForm,
        pdf_max_size_mb: parseInt(deliveryForm.pdf_max_size_mb) || 10,
        polling_interval_minutes: parseInt(deliveryForm.polling_interval_minutes) || 5,
      };
      const response = await axios.put(
        `${API_URL}/api/v1/lis-portal/labs/${selectedLab.lab_id}/integrations/${selectedIntegration.id}/config`,
        { config },
        { headers: getHeaders() }
      );
      if (response.data.success) setSuccess('Delivery settings saved');
      else setError(response.data.error);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveBranding = async () => {
    if (!selectedLab) return;
    setSaving(true);
    try {
      // Upload logo if selected
      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);
        await axios.post(
          `${API_URL}/api/v1/lis-portal/labs/${selectedLab.lab_id}/upload-logo`,
          formData,
          { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } }
        );
      }
      // Save branding config
      const response = await axios.put(
        `${API_URL}/api/v1/lis-portal/labs/${selectedLab.lab_id}/branding`,
        brandingForm,
        { headers: getHeaders() }
      );
      if (response.data.success) setSuccess('Branding saved');
      else setError(response.data.error);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  // ── Test Connection ─────────────────────────────────────────

  const handleTestConnection = async () => {
    if (!selectedLab || !selectedIntegration) return;
    setTesting(true);
    setTestResult(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/lis-portal/labs/${selectedLab.lab_id}/integrations/${selectedIntegration.id}/test`,
        {},
        { headers: getHeaders() }
      );
      setTestResult(response.data);
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data?.message || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  // ── Report Status ───────────────────────────────────────────

  const fetchReportStatus = async () => {
    if (!selectedLab) return;
    try {
      const params: any = { limit: 50 };
      if (statusFilter) params.status_filter = statusFilter;
      const response = await axios.get(
        `${API_URL}/api/v1/lis-portal/labs/${selectedLab.lab_id}/report-status`,
        { headers: getHeaders(), params }
      );
      if (response.data.success) {
        setReportStatuses(response.data.reports || []);
        setReportSummary(response.data.summary || { total: 0, pushed: 0, pending: 0, errors: 0 });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch report status');
    }
  };

  useEffect(() => {
    if (mainTab === 4 && selectedLab) fetchReportStatus();
  }, [mainTab, statusFilter]);

  // ── Render ──────────────────────────────────────────────────

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  // Lab selection
  if (!selectedLab) {
    return (
      <Box>
        <Typography variant="h5" sx={{ mb: 3 }}>Integration Setup</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {labs.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No labs found. Add a lab first from the My Labs page.</Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {labs.map((lab) => (
              <Grid item xs={12} sm={6} md={4} key={lab.id}>
                <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }} onClick={() => fetchLabDetail(lab.lab_id)}>
                  <CardContent>
                    <Typography variant="h6">{lab.name}</Typography>
                    <Typography variant="body2" color="text.secondary">Lab ID: {lab.lab_id}</Typography>
                    <Chip label={`${lab.integration_count} integration${lab.integration_count !== 1 ? 's' : ''}`}
                      size="small" color={lab.integration_count > 0 ? 'primary' : 'default'} sx={{ mt: 1 }} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  }

  // No integrations
  if (selectedLab.integrations.length === 0) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => { setSelectedLab(null); setSelectedIntegration(null); }} sx={{ mb: 2 }}>
          Back to Labs
        </Button>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>No integrations for {selectedLab.name}</Typography>
          <Typography color="text.secondary">Add an integration from the My Labs page first, then come back here to configure it.</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={() => { setSelectedLab(null); setSelectedIntegration(null); }} sx={{ mb: 1 }}>
            Back to Labs
          </Button>
          <Typography variant="h5">{selectedLab.name} - Integration Setup</Typography>
        </Box>
        {selectedLab.integrations.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Integration</InputLabel>
            <Select
              value={selectedIntegration?.id || ''}
              label="Integration"
              onChange={(e) => {
                const i = selectedLab.integrations.find(x => x.id === e.target.value);
                if (i) selectIntegration(i);
              }}
            >
              {selectedLab.integrations.map((i) => (
                <MenuItem key={i.id} value={i.id}>{i.lis_system} ({i.integration_type.toUpperCase()})</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<SettingsInputComponentIcon />} label="Connection" />
        <Tab icon={<DescriptionIcon />} label="HL7 Settings" />
        <Tab icon={<LocalShippingIcon />} label="Report Delivery" />
        <Tab icon={<PaletteIcon />} label="Branding" />
        <Tab icon={<MonitorHeartIcon />} label="Status Monitor" />
      </Tabs>

      {/* ── Connection Tab ─────────────────────────────────── */}
      {mainTab === 0 && selectedIntegration && (
        <Box>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {selectedIntegration.integration_type.toUpperCase()} Connection - {selectedIntegration.lis_system}
            </Typography>

            {selectedIntegration.integration_type === 'sftp' && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField label="Host" fullWidth size="small"
                    value={connForm.host || ''} onChange={(e) => setConnForm({ ...connForm, host: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Port" fullWidth size="small" type="number"
                    value={connForm.port || 22} onChange={(e) => setConnForm({ ...connForm, port: parseInt(e.target.value) || 22 })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Username" fullWidth size="small"
                    value={connForm.username || ''} onChange={(e) => setConnForm({ ...connForm, username: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Password" fullWidth size="small" type="password"
                    value={connForm.password || ''} onChange={(e) => setConnForm({ ...connForm, password: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Key File Path" fullWidth size="small" placeholder="/path/to/key.pem"
                    value={connForm.key_file || ''} onChange={(e) => setConnForm({ ...connForm, key_file: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Remote Path In (ORU_OUT)" fullWidth size="small"
                    helperText="Where your LIS puts HL7 files for us to pick up"
                    value={connForm.remote_path_in || ''} onChange={(e) => setConnForm({ ...connForm, remote_path_in: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Remote Path Out (ORU_IN)" fullWidth size="small"
                    helperText="Where we deliver reports back to your LIS"
                    value={connForm.remote_path_out || ''} onChange={(e) => setConnForm({ ...connForm, remote_path_out: e.target.value })} />
                </Grid>
              </Grid>
            )}

            {selectedIntegration.integration_type === 'rest' && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField label="Base URL" fullWidth size="small" placeholder="https://api.example.com"
                    value={connForm.base_url || ''} onChange={(e) => setConnForm({ ...connForm, base_url: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="API Key" fullWidth size="small"
                    value={connForm.api_key || ''} onChange={(e) => setConnForm({ ...connForm, api_key: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Timeout (seconds)" fullWidth size="small" type="number"
                    value={connForm.timeout || 30} onChange={(e) => setConnForm({ ...connForm, timeout: parseInt(e.target.value) || 30 })} />
                </Grid>
              </Grid>
            )}

            {selectedIntegration.integration_type === 'smtp' && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField label="SMTP Host" fullWidth size="small" placeholder="smtp.gmail.com"
                    value={connForm.smtp_host || ''} onChange={(e) => setConnForm({ ...connForm, smtp_host: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Port" fullWidth size="small" type="number" placeholder="587"
                    value={connForm.smtp_port || 587} onChange={(e) => setConnForm({ ...connForm, smtp_port: parseInt(e.target.value) || 587 })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Username" fullWidth size="small"
                    value={connForm.smtp_username || ''} onChange={(e) => setConnForm({ ...connForm, smtp_username: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Password" fullWidth size="small" type="password"
                    value={connForm.smtp_password || ''} onChange={(e) => setConnForm({ ...connForm, smtp_password: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="From Email" fullWidth size="small"
                    value={connForm.from_email || ''} onChange={(e) => setConnForm({ ...connForm, from_email: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="To Email" fullWidth size="small"
                    value={connForm.to_email || ''} onChange={(e) => setConnForm({ ...connForm, to_email: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={connForm.use_tls !== false} onChange={(e) => setConnForm({ ...connForm, use_tls: e.target.checked })} />}
                    label="Use TLS Encryption"
                  />
                </Grid>
              </Grid>
            )}

            {testResult && (
              <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mt: 2 }} onClose={() => setTestResult(null)}>
                {testResult.message}
              </Alert>
            )}

            {selectedIntegration.last_connected_at && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Last connected: {new Date(selectedIntegration.last_connected_at).toLocaleString()}
              </Typography>
            )}
            {selectedIntegration.last_error && (
              <Alert severity="warning" sx={{ mt: 1, py: 0 }}>{selectedIntegration.last_error}</Alert>
            )}
          </Paper>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="outlined" startIcon={testing ? <CircularProgress size={16} /> : <PlayArrowIcon />}
              onClick={handleTestConnection} disabled={testing}>
              {testing ? 'Testing...' : 'Test Connection'}
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={saveConnectionConfig} disabled={saving}>
              {saving ? 'Saving...' : 'Save Connection'}
            </Button>
          </Box>
        </Box>
      )}

      {/* ── HL7 Settings Tab ───────────────────────────────── */}
      {mainTab === 1 && (
        <Box>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>HL7 Message Configuration</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure HL7 message format settings. Defaults work for most LIS systems.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>HL7 Version</InputLabel>
                  <Select value={hl7Form.hl7_version} label="HL7 Version"
                    onChange={(e) => setHl7Form({ ...hl7Form, hl7_version: e.target.value })}>
                    <MenuItem value="2.3">2.3</MenuItem>
                    <MenuItem value="2.3.1">2.3.1</MenuItem>
                    <MenuItem value="2.4">2.4</MenuItem>
                    <MenuItem value="2.5">2.5</MenuItem>
                    <MenuItem value="2.5.1">2.5.1</MenuItem>
                    <MenuItem value="2.6">2.6</MenuItem>
                    <MenuItem value="2.7">2.7</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Encoding</InputLabel>
                  <Select value={hl7Form.hl7_encoding} label="Encoding"
                    onChange={(e) => setHl7Form({ ...hl7Form, hl7_encoding: e.target.value })}>
                    <MenuItem value="UTF-8">UTF-8</MenuItem>
                    <MenuItem value="ASCII">ASCII</MenuItem>
                    <MenuItem value="ISO-8859-1">ISO-8859-1</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}><Divider><Typography variant="caption">Separators</Typography></Divider></Grid>

              <Grid item xs={6} sm={4}>
                <TextField label="Field Separator" fullWidth size="small"
                  value={hl7Form.field_separator} onChange={(e) => setHl7Form({ ...hl7Form, field_separator: e.target.value })} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField label="Component Separator" fullWidth size="small"
                  value={hl7Form.component_separator} onChange={(e) => setHl7Form({ ...hl7Form, component_separator: e.target.value })} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField label="Repetition Separator" fullWidth size="small"
                  value={hl7Form.repetition_separator} onChange={(e) => setHl7Form({ ...hl7Form, repetition_separator: e.target.value })} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField label="Escape Character" fullWidth size="small"
                  value={hl7Form.escape_character} onChange={(e) => setHl7Form({ ...hl7Form, escape_character: e.target.value })} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField label="Subcomponent Separator" fullWidth size="small"
                  value={hl7Form.subcomponent_separator} onChange={(e) => setHl7Form({ ...hl7Form, subcomponent_separator: e.target.value })} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField label="Segment Separator" fullWidth size="small"
                  value={hl7Form.segment_separator} onChange={(e) => setHl7Form({ ...hl7Form, segment_separator: e.target.value })} />
              </Grid>
            </Grid>
          </Paper>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={saveHl7Config} disabled={saving}>
              {saving ? 'Saving...' : 'Save HL7 Settings'}
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Report Delivery Tab ────────────────────────────── */}
      {mainTab === 2 && (
        <Box>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Report Delivery Settings</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Push Method</InputLabel>
                  <Select value={deliveryForm.push_method} label="Push Method"
                    onChange={(e) => setDeliveryForm({ ...deliveryForm, push_method: e.target.value })}>
                    <MenuItem value="base64">Base64 Encoded</MenuItem>
                    <MenuItem value="binary">Binary PDF</MenuItem>
                    <MenuItem value="hl7">HL7 Embedded</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="File Naming Pattern" fullWidth size="small"
                  helperText="Use {report_id}, {lab_id}, {date} as placeholders"
                  value={deliveryForm.file_pattern} onChange={(e) => setDeliveryForm({ ...deliveryForm, file_pattern: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Max PDF Size (MB)" fullWidth size="small" type="number"
                  value={deliveryForm.pdf_max_size_mb} onChange={(e) => setDeliveryForm({ ...deliveryForm, pdf_max_size_mb: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Polling Interval (minutes)" fullWidth size="small" type="number"
                  helperText="How often to check for new HL7 files"
                  value={deliveryForm.polling_interval_minutes} onChange={(e) => setDeliveryForm({ ...deliveryForm, polling_interval_minutes: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={deliveryForm.auto_push} onChange={(e) => setDeliveryForm({ ...deliveryForm, auto_push: e.target.checked })} />}
                  label="Auto-push reports when ready"
                />
              </Grid>
            </Grid>
          </Paper>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={saveDeliveryConfig} disabled={saving}>
              {saving ? 'Saving...' : 'Save Delivery Settings'}
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Branding Tab ───────────────────────────────────── */}
      {mainTab === 3 && (
        <Box>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Lab Branding</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Customize how your lab appears on generated reports.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Logo</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {logoPreview && (
                    <Box component="img" src={logoPreview.startsWith('/') ? `${API_URL}${logoPreview}` : logoPreview}
                      sx={{ maxHeight: 60, maxWidth: 200, objectFit: 'contain', border: '1px solid #eee', borderRadius: 1, p: 0.5 }}
                      alt="Lab logo" />
                  )}
                  <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}>
                    Upload Logo
                    <input type="file" hidden accept=".png,.jpg,.jpeg,.svg,.webp"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setLogoFile(file);
                          setLogoPreview(URL.createObjectURL(file));
                        }
                      }} />
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Report Header Text" fullWidth size="small"
                  placeholder="e.g. Clinical Microbiology Report"
                  value={brandingForm.report_header_text} onChange={(e) => setBrandingForm({ ...brandingForm, report_header_text: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Primary Color" fullWidth size="small" type="color"
                  value={brandingForm.primary_color}
                  onChange={(e) => setBrandingForm({ ...brandingForm, primary_color: e.target.value })}
                  InputProps={{ startAdornment: <Box sx={{ width: 24, height: 24, bgcolor: brandingForm.primary_color, borderRadius: 0.5, mr: 1, border: '1px solid #ccc' }} /> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Secondary Color" fullWidth size="small" type="color"
                  value={brandingForm.secondary_color}
                  onChange={(e) => setBrandingForm({ ...brandingForm, secondary_color: e.target.value })}
                  InputProps={{ startAdornment: <Box sx={{ width: 24, height: 24, bgcolor: brandingForm.secondary_color, borderRadius: 0.5, mr: 1, border: '1px solid #ccc' }} /> }} />
              </Grid>
            </Grid>
          </Paper>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={saveBranding} disabled={saving}>
              {saving ? 'Saving...' : 'Save Branding'}
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Status Monitor Tab ─────────────────────────────── */}
      {mainTab === 4 && (
        <Box>
          {/* Summary */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}><Card><CardContent sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="h5">{reportSummary.total}</Typography>
              <Typography variant="caption" color="text.secondary">Total Reports</Typography>
            </CardContent></Card></Grid>
            <Grid item xs={6} sm={3}><Card><CardContent sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="h5" color="success.main">{reportSummary.pushed}</Typography>
              <Typography variant="caption" color="text.secondary">Pushed</Typography>
            </CardContent></Card></Grid>
            <Grid item xs={6} sm={3}><Card><CardContent sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="h5" color="warning.main">{reportSummary.pending}</Typography>
              <Typography variant="caption" color="text.secondary">Pending</Typography>
            </CardContent></Card></Grid>
            <Grid item xs={6} sm={3}><Card><CardContent sx={{ textAlign: 'center', py: 1 }}>
              <Typography variant="h5" color="error.main">{reportSummary.errors}</Typography>
              <Typography variant="caption" color="text.secondary">Errors</Typography>
            </CardContent></Card></Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter</InputLabel>
              <Select value={statusFilter} label="Filter" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pushed">Pushed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="error">Errors</MenuItem>
              </Select>
            </FormControl>
            <Button startIcon={<RefreshIcon />} onClick={fetchReportStatus}>Refresh</Button>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Report ID</TableCell>
                  <TableCell>LIS</TableCell>
                  <TableCell>Panel</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell align="center">Attempts</TableCell>
                  <TableCell>Error</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportStatuses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No reports found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  reportStatuses.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell><Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.report_id}</Typography></TableCell>
                      <TableCell>{r.lis_system}</TableCell>
                      <TableCell>{r.panel_type || '-'}</TableCell>
                      <TableCell align="center">
                        <Chip label={r.is_pushed ? 'Pushed' : 'Pending'} size="small"
                          color={r.is_pushed ? 'success' : 'warning'} />
                      </TableCell>
                      <TableCell>{r.push_method || '-'}</TableCell>
                      <TableCell align="center">{r.push_attempts}</TableCell>
                      <TableCell>
                        {r.last_error ? (
                          <Tooltip title={r.last_error}>
                            <Typography variant="body2" color="error" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {r.last_error}
                            </Typography>
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default IntegrationSetup;
